import { useMemo } from 'react';
import { useList } from '@refinedev/core';
import { useQuery } from '@tanstack/react-query';
import type { Conference, User } from '@viq-admin/types';
import type { CrmConnectionRow } from '@viq-admin/screens';
import { fetchTelemetryInstalls } from '../../../api/telemetry';
import { integrationsApi } from '../../../api/integrationsApi';

/**
 * The company page's reads. Every one names THIS company explicitly, never
 * the global "Operate as" scope, so the page shows the same data whatever
 * the picker says (plan rule: "Explicit company, never the global scope, on
 * hub pages"). What each `companyId` means was checked against viq-backend
 * (apps/superadmin-console/src), noted per hook.
 *
 * Each hook is called by the component that renders its data. antd's Tabs
 * mount a pane only once it is first opened, so a tab's fetch runs when the
 * tab is shown; react-query shares one request between the Overview card and
 * its tab (same key), and the page's tab-label counts reuse the users and
 * conferences queries.
 */

const NO_USERS: User[] = [];
const NO_CONFERENCES: Conference[] = [];
const NO_CONNECTIONS: CrmConnectionRow[] = [];

/**
 * GET /v1/superadmin/users?companyId= — handlers/users.ts → db/userProfiles
 * `listUsers`: `WHERE company_id = $companyId`, active AND deactivated users
 * (the "Anonymized rep" sentinel excluded), ordered by name.
 *
 * `meta.companyId` is the dataProvider's explicit override: a caller that
 * passes the key always wins over the global scope (api/dataProvider.ts
 * getList).
 */
export function useCompanyUsers(companyId: string | undefined) {
  const { result, query } = useList<User>({
    resource: 'users',
    pagination: { mode: 'off' },
    meta: { companyId },
    queryOptions: { enabled: !!companyId },
  });
  return {
    users: result?.data ?? NO_USERS,
    loaded: query.isSuccess,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * GET /v1/superadmin/conferences?companyId= — handlers/conferences.ts →
 * db/conferences `listConferences`: an INNER JOIN on company_conferences, so
 * it returns the conferences this company has been GRANTED access to (not
 * ones it "owns"; conferences span grantee tenants). Active conferences only
 * (`archived_at IS NULL`, since `archived` is not passed). Dates are
 * `YYYY-MM-DD`.
 */
export function useCompanyConferences(companyId: string | undefined) {
  const { result, query } = useList<Conference>({
    resource: 'conferences',
    pagination: { mode: 'off' },
    meta: { companyId },
    queryOptions: { enabled: !!companyId },
  });
  return {
    conferences: result?.data ?? NO_CONFERENCES,
    loaded: query.isSuccess,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * GET /v1/superadmin/telemetry/installs?companyId= — handlers/telemetry.ts →
 * db/deviceTelemetry `listDeviceInstalls`: `WHERE device_installs.company_id
 * = $companyId` (the company the app stamped at sign-in), most recently seen
 * first. `stranded` and `outdated` are computed server-side; `latestVersion`
 * in the response is the FLEET's newest version, not this company's.
 *
 * Same query key as the Telemetry page (`['telemetryInstalls', companyId]`,
 * same fetcher and argument), so the two share a cache entry.
 */
export function useCompanyInstalls(companyId: string | undefined) {
  return useQuery({
    queryKey: ['telemetryInstalls', companyId],
    queryFn: () => fetchTelemetryInstalls({ companyId: companyId! }),
    enabled: !!companyId,
  });
}

/**
 * GET /v1/superadmin/crm/connections — handlers/crmConnections.ts
 * `listCrmConnectionsCore` takes NO company filter: it returns every
 * tenant's connections. So this narrows client-side on the row's
 * `companyId`. The list is small (at most one row per company per CRM
 * source).
 *
 * Same key and fetcher as the Integrations list (`ConnectionsTable`), so a
 * connection created or deleted there invalidates this too.
 */
export function useCompanyCrmConnections(companyId: string | undefined) {
  const query = useQuery({
    queryKey: ['integrationsConnections'],
    queryFn: integrationsApi.listConnections,
    enabled: !!companyId,
  });
  const all = query.data ?? NO_CONNECTIONS;
  const connections = useMemo(
    () => all.filter((c) => c.companyId === companyId),
    [all, companyId],
  );
  return {
    connections,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
